/**
 * Kory pointing at things on any page by role and accessible name: the targets `point_to_todos` names
 * (`region "Cash"`, `row "Cash collected"`), written as the screen digest writes them. They arrive in
 * the reply's `sections` list beside Overview's section keys (a key never has a space) and page parts
 * (`kory-refs`). Pointing marks the element (`data-kory-pointed`) and scrolls it into view; it never
 * clicks, types or opens anything.
 */
import { computeAccessibleName, getRole } from "dom-accessibility-api";

import { isPagePart } from "@/lib/kory-refs";
import { hiddenFromView } from "@/lib/kory-screen-digest";

/** The roles Kory can point at; the backend's `references.POINTABLE_ROLES` is the same list (a test keeps them together). */
export const POINTABLE_ROLES = [
  "region",
  "article",
  "navigation",
  "dialog",
  "heading",
  "table",
  "grid",
  "row",
  "cell",
  "gridcell",
  "columnheader",
  "rowheader",
  "listitem",
  "tab",
  "tabpanel",
  "group",
  "figure",
  "img",
  "button",
  "link",
] as const;

type PointableRole = (typeof POINTABLE_ROLES)[number];

/** Elements that can carry each role, natively or by `role=`; `getRole` then confirms it. */
const CANDIDATES: Record<PointableRole, string> = {
  region: 'section, [role="region"]',
  article: 'article, [role="article"]',
  navigation: 'nav, [role="navigation"]',
  dialog: '[role="dialog"], [role="alertdialog"], dialog',
  heading: 'h1, h2, h3, h4, h5, h6, [role="heading"]',
  table: 'table, [role="table"]',
  grid: '[role="grid"], [role="treegrid"]',
  row: 'tr, [role="row"]',
  cell: 'td, [role="cell"]',
  gridcell: 'td, [role="gridcell"]',
  columnheader: 'th, [role="columnheader"]',
  rowheader: 'th, [role="rowheader"]',
  listitem: 'li, [role="listitem"]',
  tab: '[role="tab"]',
  tabpanel: '[role="tabpanel"]',
  group: 'fieldset, details, [role="group"]',
  figure: 'figure, [role="figure"]',
  img: 'img, svg[aria-label], [role="img"]',
  button: 'button, [role="button"], input[type="button"], input[type="submit"]',
  link: 'a[href], [role="link"]',
};

const TARGET = /^([a-z]+) "(.+)"$/s;

export interface PointTarget {
  role: PointableRole;
  name: string;
}

/** A `role "name"` entry as a target; null for a section key or a role Kory can't point at. */
export function parseTarget(entry: string): PointTarget | null {
  const match = TARGET.exec(entry);
  if (!match || !(POINTABLE_ROLES as readonly string[]).includes(match[1])) return null;
  return { role: match[1] as PointableRole, name: match[2] };
}

const norm = (text: string) => text.replace(/\s+/g, " ").trim().toLowerCase();

const wanted = (target: PointTarget) => norm(target.name).replace(/…$/, "").trimEnd();

/** How `el` fits `target`: its exact name, or a name that starts with it at a word break (a row by its
 *  first cell, a name the digest shortened with "…"). Null when it doesn't fit, or is hidden. */
function fit(el: Element, role: PointableRole, want: string): "exact" | "start" | null {
  if (!want || getRole(el) !== role || hiddenFromView(el)) return null;
  const name = norm(computeAccessibleName(el));
  if (name === want) return "exact";
  return name.startsWith(want) && /^\W/.test(name.slice(want.length)) ? "start" : null;
}

/** The element `target` names in `root`: an exact name, else the one element whose name starts with it.
 *  Two that start with it is no match: Kory never points at a guess. Hidden elements never match. */
export function findTarget(target: PointTarget, root: ParentNode = document): HTMLElement | null {
  const want = wanted(target);
  const starts: HTMLElement[] = [];
  for (const el of root.querySelectorAll<HTMLElement>(CANDIDATES[target.role])) {
    const how = fit(el, target.role, want);
    if (how === "exact") return el;
    if (how) starts.push(el);
  }
  return starts.length === 1 ? starts[0] : null;
}

/** True when ``entry`` in a reply's ``sections`` is something Kory points at on the page (a `role "name"`
 *  target or a page part), not an Overview section key. */
export function isTarget(entry: string): boolean {
  return parseTarget(entry) !== null || isPagePart(entry);
}

/** `el` still is what `target` names: on the page, shown, and with a name that fits. */
export function stillTarget(el: Element, target: PointTarget): boolean {
  return el.isConnected && fit(el, target.role, wanted(target)) !== null;
}
